import { Component, Injector, Input, OnInit } from '@angular/core';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * TableDummyHalf Widget
 */
@Component({
  selector: 'table-dummy-half-widget',
  templateUrl: './table-dummy-half-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../table-dummies-widget-base/table-dummies-widget-base.scss']
})
export class TableDummyHalfWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'tableDummyHalf', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler(this.name, this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;
    this.availableLayouts = this.widgetConfig.widgetSettings.availableLayouts;

    //we need 300 calculations/layouts (as defined in maxCalculations)
    const supportedCalculationsNumber = this.widgetConfig.widgetSettings.maxCalculations;
    const range = Array.from({ length: supportedCalculationsNumber }, (_value, key) => (key + 1));
    this.availableCalculations = range.map(n => ({ id: n, description: `Table Dummy Half ${n}` }));
    this.widgetConfig.widgetSettings.availableCalculations = this.availableCalculations;

    if (this.widgetConfig.widgetSettings.currentCalculation === -1) {
      this.chooseNextCalculation();
    }

    this.mockupData$ = this.widgetService.loadMockup(this.name);

    const currentCalculationId = this.widgetConfig.widgetSettings.currentCalculation;
    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load(this.name, this.getReport(), `calculationId=${currentCalculationId}`);
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    data.rows.forEach((item, idx) => {
      item.trackId = idx;
    });

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;
        }
        if (config.y) {
          this.widgetConfig.rows = config.y;
        }
        if (this.page.options.api?.optionsChanged) {
          this.page.options.api.optionsChanged();
        }
      }

      this.widgetConfig.widgetSettings.currentLayout = newLayout;
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Calculation changed event handler
  * @param newCalculation {number} New Calculation
   */
  public calculationChanged(newCalculation: number): void {
    try {
      if (this.widgetConfig?.widgetSettings?.currentCalculation) {
        this.widgetConfig.widgetSettings.currentCalculation = newCalculation;
        this.currentCalculation = newCalculation;
      }
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any): void {
    //do something if error
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param item {any} Row item
   */
  public trackById(_index: number, item: any): number {
    return item.trackId;
  }

  /**
   * Choose next available layout when adding text widget into report document
   */
  private chooseNextCalculation() {
    const activeDocument = this.rdStorage.getActiveDocument();
    const pages = activeDocument.data.pages;
    const myWidgetId = this.widgetConfig.widgetId;
    const widgetsOfMyType = [...pages.map(p => p.widgets)].flat().filter(widget => widget.widgetId === myWidgetId);

    const usedCalculations = widgetsOfMyType.map(widget => widget.widgetSettings.currentCalculation);
    const choosableCalculations = this.availableCalculations.filter(calculation => usedCalculations.indexOf(calculation.id) === -1);
    const nextId = choosableCalculations[0].id;
    this.widgetConfig.widgetSettings.currentCalculation = nextId;
    this.currentCalculation = nextId;

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    })
  }
}
